// ─── Time / Date formatting helpers ───────────────────────────
// Single source of truth for all user-visible date/time strings.

/**
 * formatTime("14:30") → "2:30 PM"
 * Accepts "HH:MM" 24-hour strings (as stored / returned by the API).
 */
export function formatTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

/**
 * formatDate(date) → "Mon, Jan 1, 2024"
 * Date-only; no time component.
 */
export function formatDate(date) {
  if (!(date instanceof Date)) return "";
  return date.toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

/**
 * formatEventDateTime(date) → "Mon, Jan 1, 2024, 3:00 PM"
 * Combined date + time for event cards and pickers.
 */
export function formatEventDateTime(date) {
  if (!(date instanceof Date) || isNaN(date)) return "";
  return (
    date.toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  );
}

/**
 * formatMonthYear(date) → "January 2024"
 * Used for "Member since" and similar long-form month labels.
 */
export function formatMonthYear(date) {
  if (!(date instanceof Date)) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

/**
 * toTimestamp(value) → number | null
 * Converts a date string or Date to a numeric timestamp for sorting.
 * Returns null for missing/invalid values so sort comparators can push them to the end.
 */
export function toTimestamp(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return isNaN(t) ? null : t;
}

/**
 * formatDateParts(date) → { month: "Jan", day: 5 }
 * Used by EventDateBadge-style components that display month/day stacked.
 */
export function formatDateParts(date) {
  const d = date instanceof Date ? date : new Date(date);
  return {
    month: d.toLocaleString("default", { month: "short" }),
    day:   d.getDate(),
  };
}
